import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'motion/react';
import Lenis from 'lenis';
import {
  downloadOriginalPdf,
  fetchCatalogDocuments,
  fetchLayout,
  fetchLayouts,
  fetchProducts,
  type CatalogDocumentVariant,
  type LayoutDetail,
  type LayoutListItem,
  type ProductItem,
} from '../lib/api';
import { formatCode, formatCents, formatDimensions } from '../lib/format';
import { PlaceholderRender } from '../components/PlaceholderRender';
import { Lightbox } from '../components/Lightbox';
import { AddToDeckButton } from '../components/AddToDeckButton';
import { useDeck } from '../lib/deckContext';
import { GRID_RENDER_KINDS, DECK_RENDER_KINDS, RENDER_LABELS, type RenderKind } from '../lib/deck';

export function Gallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const familySlug = searchParams.get('family');

  const [layouts, setLayouts] = useState<LayoutListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductItem | null>(null);
  const [productQuery, setProductQuery] = useState('');
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [originalExportInFlight, setOriginalExportInFlight] = useState(false);
  const [copiedLink, setCopiedLink] = useState<'with' | 'without' | 'original' | null>(null);
  const [catalogDocs, setCatalogDocs] = useState<CatalogDocumentVariant[]>([]);

  const copyLink = async (key: 'with' | 'without' | 'original', url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(key);
      setTimeout(() => setCopiedLink(null), 1800);
    } catch {
      // Clipboard not available
    }
  };

  // Index of the currently-focused (centered) layout, or null when the cover
  // is in view.
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const focusedIdxRef = useRef<number | null>(null);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const lenisRef = useRef<Lenis | null>(null);

  // Set to true when familySlug changes while the user was already browsing
  // layouts (not parked on the cover). After the new layouts render, we land
  // on the first layout of the new filter instead of resetting to the top.
  const wantsFirstLayoutOnLoadRef = useRef(false);

  // Deep-link support: `/?layout=<code>` scrolls to and focuses that layout on
  // load. Captured once on mount so later filter changes don't re-trigger it.
  const initialLayoutParamRef = useRef(searchParams.get('layout'));

  useEffect(() => {
    fetchProducts()
      .then((r) => setProducts(r.products))
      .catch(() => {/* non-fatal — search bar just stays empty */});
  }, []);

  // Which golden catalog PDFs the Visualizer has published (drives the
  // Photorealistic download card). Non-fatal — unpublished variants stay "soon".
  useEffect(() => {
    fetchCatalogDocuments()
      .then((r) => setCatalogDocs(r.variants))
      .catch(() => {/* leave empty → all variants show as coming soon */});
  }, []);

  useEffect(() => {
    let cancelled = false;
    wantsFirstLayoutOnLoadRef.current = focusedIdxRef.current !== null;

    setLayouts(null);
    focusedIdxRef.current = null;
    setFocusedIdx(null);
    fetchLayouts(familySlug ?? undefined, selectedProduct?.id)
      .then((r) => !cancelled && setLayouts(r.layouts))
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
  }, [familySlug, selectedProduct]);

  useEffect(() => {
    const wrapper = scrollerRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    const lenis = new Lenis({
      wrapper,
      content,
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.6,
      lerp: 0.08,
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    lenisRef.current = lenis;

    let rafId = requestAnimationFrame(function tick(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(tick);
    });

    let isSnapping = false;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const targetForSection = (section: HTMLElement, viewportH: number): number => {
      const raw = section.offsetTop + (section.clientHeight - viewportH) / 2;
      return Math.max(0, Math.min(lenis.limit, raw));
    };

    const findNearestSnapTarget = (currentScroll: number): number => {
      const sections = content.querySelectorAll<HTMLElement>('[data-snap-page]');
      const viewportH = wrapper.clientHeight;
      let best = currentScroll;
      let bestDist = Infinity;
      sections.forEach((section) => {
        const target = targetForSection(section, viewportH);
        const dist = Math.abs(currentScroll - target);
        if (dist < bestDist) {
          bestDist = dist;
          best = target;
        }
      });
      return best;
    };

    const updateFocused = (currentScroll: number): void => {
      const layoutSections = content.querySelectorAll<HTMLElement>('[data-layout-index]');
      if (layoutSections.length === 0) {
        if (focusedIdxRef.current !== null) {
          focusedIdxRef.current = null;
          setFocusedIdx(null);
        }
        return;
      }
      const viewportH = wrapper.clientHeight;
      const viewportCenter = currentScroll + viewportH / 2;
      let bestIdx = -1;
      let bestDist = Infinity;
      layoutSections.forEach((sec) => {
        const center = sec.offsetTop + sec.clientHeight / 2;
        const dist = Math.abs(center - viewportCenter);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = parseInt(sec.dataset.layoutIndex ?? '0', 10);
        }
      });
      // If the closest layout is still more than ~half a viewport away,
      // the user is up on the cover — no layout focused.
      if (bestDist > viewportH * 0.55 || bestIdx < 0) {
        if (focusedIdxRef.current !== null) {
          focusedIdxRef.current = null;
          setFocusedIdx(null);
        }
        return;
      }
      if (bestIdx !== focusedIdxRef.current) {
        focusedIdxRef.current = bestIdx;
        setFocusedIdx(bestIdx);
      }
    };

    const snapToNearest = (): void => {
      if (isSnapping) return;
      const viewportH = wrapper.clientHeight;
      if (viewportH === 0) return;
      const current = lenis.scroll;
      const target = findNearestSnapTarget(current);
      const distance = Math.abs(current - target);
      if (distance < 1.5) return;
      const duration = Math.min(0.8, 0.35 + (distance / viewportH) * 0.4);
      isSnapping = true;
      lenis.scrollTo(target, {
        duration,
        easing: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,
        onComplete: () => {
          isSnapping = false;
        },
      });
    };

    const onScroll = (event: { velocity: number; scroll: number }): void => {
      updateFocused(event.scroll);
      if (isSnapping) return;
      if (Math.abs(event.velocity) > 1.4) {
        if (idleTimer) clearTimeout(idleTimer);
        return;
      }
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(snapToNearest, 0);
    };

    const onUserInput = (): void => {
      isSnapping = false;
      if (idleTimer) clearTimeout(idleTimer);
    };

    lenis.on('scroll', onScroll);
    wrapper.addEventListener('wheel', onUserInput, { passive: true });
    wrapper.addEventListener('touchstart', onUserInput, { passive: true });

    // Drag-to-scroll
    let isDragging = false;
    let dragStartY = 0;
    let dragStartScroll = 0;

    const onMouseDown = (e: MouseEvent): void => {
      if ((e.target as Element).closest('a, button, input, [data-no-drag]')) return;
      isDragging = true;
      dragStartY = e.clientY;
      dragStartScroll = lenis.scroll;
      isSnapping = false;
      if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
      wrapper.style.cursor = 'grabbing';
      wrapper.style.userSelect = 'none';
    };

    const onMouseMove = (e: MouseEvent): void => {
      if (!isDragging) return;
      e.preventDefault();
      const delta = dragStartY - e.clientY;
      lenis.scrollTo(dragStartScroll + delta, { immediate: true });
    };

    const onMouseUp = (): void => {
      if (!isDragging) return;
      isDragging = false;
      wrapper.style.cursor = '';
      wrapper.style.userSelect = '';
      idleTimer = setTimeout(snapToNearest, 80);
    };

    wrapper.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    // Initial focus calculation (in case content is already at a snap point)
    updateFocused(lenis.scroll);

    return () => {
      cancelAnimationFrame(rafId);
      if (idleTimer) clearTimeout(idleTimer);
      wrapper.removeEventListener('wheel', onUserInput);
      wrapper.removeEventListener('touchstart', onUserInput);
      wrapper.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // After new layouts populate (either initial load or filter change), scroll
  // to either the cover (top) or the first layout of the new filter, based on
  // whether the user was already browsing when they switched.
  useEffect(() => {
    if (!layouts) return;
    const wrapper = scrollerRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    // Wait one frame so React commits the new DOM and Lenis can re-measure.
    requestAnimationFrame(() => {
      const lenis = lenisRef.current;
      if (!lenis) return;
      lenis.resize();

      // Deep link: `/?layout=<code>` lands directly on that layout (highest
      // priority, consumed once). Then strip the param so the URL is clean and
      // later filter changes behave normally.
      const deepLinkCode = initialLayoutParamRef.current;
      if (deepLinkCode) {
        initialLayoutParamRef.current = null;
        setSearchParams(
          (p) => {
            p.delete('layout');
            return p;
          },
          { replace: true },
        );
        const idx = layouts.findIndex((l) => l.code === deepLinkCode);
        if (idx >= 0) {
          const section = content.querySelector<HTMLElement>(`[data-layout-index="${idx}"]`);
          if (section) {
            const viewportH = wrapper.clientHeight;
            const target = Math.max(0, Math.min(lenis.limit, section.offsetTop + (section.clientHeight - viewportH) / 2));
            lenis.scrollTo(target, { immediate: true });
            return;
          }
        }
      }

      // Restore scroll position when returning from a layout detail page.
      const returnCode = sessionStorage.getItem('gallery:returnTo');
      if (returnCode) {
        sessionStorage.removeItem('gallery:returnTo');
        const returnIdx = layouts.findIndex((l) => l.code === returnCode);
        if (returnIdx >= 0) {
          const section = content.querySelector<HTMLElement>(`[data-layout-index="${returnIdx}"]`);
          if (section) {
            const viewportH = wrapper.clientHeight;
            const target = Math.max(0, Math.min(lenis.limit, section.offsetTop + (section.clientHeight - viewportH) / 2));
            lenis.scrollTo(target, { immediate: true });
            return;
          }
        }
      }

      const wantsFirstLayout = wantsFirstLayoutOnLoadRef.current && layouts.length > 0;
      wantsFirstLayoutOnLoadRef.current = false;

      if (wantsFirstLayout) {
        const firstLayout = content.querySelector<HTMLElement>('[data-layout-index="0"]');
        if (firstLayout) {
          const viewportH = wrapper.clientHeight;
          const target = Math.max(
            0,
            Math.min(
              lenis.limit,
              firstLayout.offsetTop + (firstLayout.clientHeight - viewportH) / 2,
            ),
          );
          lenis.scrollTo(target, { duration: 0.7 });
          return;
        }
      }
      lenis.scrollTo(0, { duration: 0.7 });
    });
  }, [layouts]);

  const handleOriginalDownload = async (): Promise<void> => {
    setOriginalExportInFlight(true);
    try {
      await downloadOriginalPdf();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setOriginalExportInFlight(false);
    }
  };

  // Cover CTA: smooth-scroll to the first layout (centered), same target math
  // as the scroll-restore effect.
  const scrollToFirstLayout = (): void => {
    const lenis = lenisRef.current;
    const wrapper = scrollerRef.current;
    const content = contentRef.current;
    if (!lenis || !wrapper || !content) return;
    const section = content.querySelector<HTMLElement>('[data-layout-index="0"]');
    if (!section) return;
    const viewportH = wrapper.clientHeight;
    const target = Math.max(0, Math.min(lenis.limit, section.offsetTop + (section.clientHeight - viewportH) / 2));
    lenis.scrollTo(target, { duration: 0.9 });
  };

  const focusedLayout =
    layouts && focusedIdx !== null ? (layouts[focusedIdx] ?? null) : null;

  return (
    <div className="h-full grid grid-cols-2">
      {/* LEFT: scrolling flipbook */}
      <div ref={scrollerRef} className="h-full overflow-y-auto bg-vari-beige/40 cursor-grab active:cursor-grabbing">
        {/* Product search bar — sticky inside the scroll container */}
        <div className="sticky top-0 z-20" data-no-drag>
          <ProductSearchBar
            products={products}
            selected={selectedProduct}
            query={productQuery}
            dropdownOpen={productDropdownOpen}
            onQueryChange={(q) => { setProductQuery(q); setProductDropdownOpen(true); }}
            onSelect={(p) => { setSelectedProduct(p); setProductQuery(p.name); setProductDropdownOpen(false); }}
            onClear={() => { setSelectedProduct(null); setProductQuery(''); setProductDropdownOpen(false); }}
            onDropdownOpen={() => setProductDropdownOpen(true)}
            onDropdownClose={() => setProductDropdownOpen(false)}
          />
        </div>
        <div ref={contentRef}>
          {/* Cover */}
          <section
            data-snap-page
            className="h-[100dvh] min-h-[600px] flex items-center justify-center px-6"
          >
            <div className="max-w-xl w-full text-center space-y-7">
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.3em] text-vari-blue-accessible">
                  Vari Idea Starter · April 2026
                </p>
                <h1 className="text-4xl sm:text-5xl font-semibold text-vari-navy tracking-tight">
                  Browse the catalog
                </h1>
                <p className="text-vari-gray">
                  82 pre-designed office layouts across six families. Scroll to flip through pages —
                  details appear on the right.
                </p>
              </div>
              {/* Family filter chips moved to the top nav bar so they stay
                  visible while scrolling. The cover keeps just the title +
                  full-catalog export options below. */}

              {/* Downloads */}
              <div className="border-t border-vari-soft-gray/60 pt-5">
                <p className="text-[11px] uppercase tracking-[0.25em] text-vari-gray mb-3">
                  Downloads
                </p>
                {/* Two boxes. Photorealistic = the four golden PDFs published by
                    the Visualizer (people × pricing), linked from /downloads when
                    available. Original = the source catalog PDF; its "with pricing"
                    is the current source file, "without pricing" awaits a file. */}
                <div className="grid grid-cols-2 gap-3 text-left">
                  {/* Photorealistic — four golden PDFs published by the Visualizer:
                      with-people / without-people, each ± pricing. */}
                  <div className="rounded-lg border border-vari-soft-gray p-4 space-y-3">
                    <div className="min-h-[3.25rem]">
                      <p className="text-xs font-semibold text-vari-navy">Photorealistic</p>
                      <p className="text-[11px] text-vari-gray leading-snug">AI-rendered catalog — all 82 rooms.</p>
                    </div>
                    {[
                      { people: true, label: 'With people' },
                      { people: false, label: 'Without people' },
                    ].map((group) => (
                      <div key={group.label} className="space-y-1.5">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-vari-gray/60">{group.label}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { pricing: true, label: 'With pricing' },
                            { pricing: false, label: 'Without pricing' },
                          ].map((opt) => {
                            const v = catalogDocs.find(
                              (d) => d.people === group.people && d.pricing === opt.pricing,
                            );
                            return v?.available && v.url ? (
                              <a
                                key={opt.label}
                                href={v.url}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded border border-vari-navy/60 text-vari-navy text-[11px] font-medium px-2 py-1.5 text-center hover:bg-vari-navy hover:text-white transition-colors"
                              >
                                {opt.label}
                              </a>
                            ) : (
                              <span
                                key={opt.label}
                                title="Coming soon"
                                className="rounded border border-dashed border-vari-soft-gray text-vari-gray/45 text-[11px] px-2 py-1.5 text-center cursor-not-allowed"
                              >
                                {opt.label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Original document */}
                  <div className="rounded-lg border border-vari-soft-gray p-4 space-y-3">
                    <div className="min-h-[3.25rem]">
                      <p className="text-xs font-semibold text-vari-navy">Original document</p>
                      <p className="text-[11px] text-vari-gray leading-snug">The source Vari Idea Starter catalog (PDF).</p>
                    </div>
                    {/* With pricing — current source PDF */}
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={handleOriginalDownload}
                        disabled={originalExportInFlight}
                        className="rounded bg-vari-navy text-white px-3 py-1.5 text-xs font-semibold hover:bg-vari-blue-accessible transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {originalExportInFlight ? 'Downloading…' : 'With pricing'}
                      </button>
                      <button
                        type="button"
                        onClick={() => copyLink('original', `${window.location.origin}/api/exports/original`)}
                        className="text-[11px] text-vari-blue-accessible hover:underline text-left"
                      >
                        {copiedLink === 'original' ? '✓ copied' : 'copy link'}
                      </button>
                    </div>
                    {/* Without pricing — awaiting the no-pricing source file */}
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        disabled
                        title="Coming soon"
                        className="rounded border border-vari-soft-gray text-vari-gray/50 px-3 py-1.5 text-xs font-medium cursor-not-allowed"
                      >
                        Without pricing
                      </button>
                      <span className="text-[11px] text-vari-gray/60">coming soon</span>
                    </div>
                  </div>
                </div>
              </div>

              {layouts && layouts.length > 0 && (
                <div className="pt-3">
                  <button
                    type="button"
                    onClick={scrollToFirstLayout}
                    className="inline-flex items-center gap-2 rounded-full bg-vari-navy text-white px-6 py-2.5 text-sm font-semibold hover:bg-vari-blue-accessible transition-colors shadow-sm"
                  >
                    Start browsing
                    <span aria-hidden>↓</span>
                  </button>
                </div>
              )}
            </div>
          </section>

          {error && (
            <section
              data-snap-page
              className="h-[100dvh] flex items-center justify-center px-6"
            >
              <div className="rounded-md border border-vari-red bg-vari-red/10 text-vari-red px-6 py-4 text-sm">
                {error}
              </div>
            </section>
          )}

          {layouts === null ? null : layouts.length === 0 ? (
            <section
              data-snap-page
              className="h-[100dvh] flex items-center justify-center"
            >
              <p className="text-vari-gray">No layouts found.</p>
            </section>
          ) : (
            <div className="pt-[4vh] pb-[44vh]">
              {layouts.map((layout, i) => (
                <LayoutPage
                  key={layout.id}
                  layout={layout}
                  index={i}
                  pageIndex={i + 1}
                  totalPages={layouts.length}
                  scrollerRef={scrollerRef}
                />
              ))}
            </div>
          )}
        </div>
      </div>{/* end scrollerRef */}

      {/* RIGHT: focused-layout detail panel (static, no scroll) */}
      <FocusPanel layout={focusedLayout} />
    </div>
  );
}

function LayoutPage({
  layout,
  index,
  pageIndex,
  totalPages,
  scrollerRef,
}: {
  layout: LayoutListItem;
  index: number;
  pageIndex: number;
  totalPages: number;
  scrollerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    container: scrollerRef,
    target: ref,
    offset: ['start end', 'end start'],
  });

  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [10, 0, -10]);
  const y = useTransform(scrollYProgress, [0, 0.5, 1], [12, 0, -12]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.5, 0.8, 1], [0.35, 0.8, 1, 0.8, 0.35]);
  // Scale falloff is gentle now — neighbors stay nearly the same size as
  // the focused card; the visual hierarchy is driven by shadow depth below.
  const scale = useTransform(
    scrollYProgress,
    [0, 0.2, 0.5, 0.8, 1],
    [0.85, 0.95, 1, 0.95, 0.85],
  );
  // Shadow grows from "flat against the page" at the edges into a deep
  // navy-tinted drop at center. The focused card visually lifts off the
  // surface; neighbors sit flush, anchoring the wheel.
  const boxShadow = useTransform(
    scrollYProgress,
    [0, 0.2, 0.5, 0.8, 1],
    [
      '0 0 0 0 rgba(0, 51, 73, 0)',
      '0 6px 20px -4px rgba(0, 51, 73, 0.25)',
      '0 24px 50px -8px rgba(0, 51, 73, 0.4)',
      '0 6px 20px -4px rgba(0, 51, 73, 0.25)',
      '0 0 0 0 rgba(0, 51, 73, 0)',
    ],
  );

  return (
    <section
      ref={ref}
      data-snap-page
      data-layout-index={index}
      className="h-[min(60vh,580px)] min-h-[520px] flex items-center justify-center px-8 relative"
      style={{ perspective: 2000 }}
    >
      <PageCard
        layout={layout}
        rotateX={rotateX}
        y={y}
        opacity={opacity}
        scale={scale}
        boxShadow={boxShadow}
        pageIndex={pageIndex}
        totalPages={totalPages}
        scrollYProgress={scrollYProgress}
      />
    </section>
  );
}

function PageCard({
  layout,
  rotateX,
  y,
  opacity,
  scale,
  boxShadow,
  pageIndex,
  totalPages,
  scrollYProgress,
}: {
  layout: LayoutListItem;
  rotateX: MotionValue<number>;
  y: MotionValue<number>;
  opacity: MotionValue<number>;
  scale: MotionValue<number>;
  boxShadow: MotionValue<string>;
  pageIndex: number;
  totalPages: number;
  scrollYProgress: MotionValue<number>;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const counterOpacity = useTransform(scrollYProgress, [0.35, 0.5, 0.65], [0, 1, 0]);
  // Full-res image for the lightbox: same precedence as the card hero (master
  // first, else first environmental) but the original instead of the thumbnail.
  const heroFull =
    layout.renders.master?.fullUrl ??
    ['environmental-1', 'environmental-2', 'environmental-3', 'environmental-4', 'environmental-5']
      .map((k) => layout.renders[k]?.fullUrl)
      .find(Boolean) ??
    layout.heroUrl;
  return (
    <motion.div
      style={{ rotateX, y, opacity, scale, boxShadow, transformStyle: 'preserve-3d' }}
      className="w-full max-w-2xl rounded-2xl overflow-hidden"
    >
      <button
        type="button"
        className="block relative w-full cursor-pointer"
        onClick={() => setLightboxOpen(true)}
      >
        <PlaceholderRender
          aspectClass="aspect-[4/3]"
          label={`#${formatCode(layout.code)}`}
          src={layout.heroUrl}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-10">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 mb-1">
            {layout.familyName}
          </p>
          <h2 className="text-2xl font-semibold text-white leading-tight">
            #{formatCode(layout.code)}
          </h2>
          <p className="text-white/60 text-xs mt-1">
            {formatDimensions(layout.widthInches, layout.depthInches)} · {layout.itemCount} items
          </p>
        </div>
        <motion.div
          style={{ opacity: counterOpacity }}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[9px] text-white/50 tracking-[0.35em] pointer-events-none select-none"
        >
          {pageIndex} / {totalPages}
        </motion.div>
      </button>

      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            // Card uses heroUrl (the small thumbnail) for fast scrolling; the
            // expanded lightbox uses the full-res original so it stays crisp.
            src={heroFull}
            label={`#${formatCode(layout.code)} — ${layout.familyName}`}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function FocusPanel({ layout }: { layout: LayoutListItem | null }) {
  return (
    <div className="h-full overflow-hidden bg-white border-l border-vari-soft-gray flex flex-col">
      {/* Direct swap, no AnimatePresence mode="wait": serialized exit->enter
          transitions deadlock the panel when the focused layout changes faster
          than the animation (fast scrolling), leaving stale info on the right.
          FocusedLayoutView still animates in on mount (keyed by layout code). */}
      {layout ? (
        <FocusedLayoutView key={layout.code} layout={layout} />
      ) : (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="h-full flex items-center justify-center px-10 text-center"
          >
            <div className="space-y-3 max-w-sm">
              <p className="text-xs uppercase tracking-[0.3em] text-vari-blue-accessible">
                Idea Starter
              </p>
              <p className="text-2xl font-semibold text-vari-navy tracking-tight">
                Scroll to browse layouts
              </p>
              <p className="text-vari-gray text-sm">
                Each layout's renders and pricing will appear here as you flip through the catalog
                on the left.
              </p>
            </div>
          </motion.div>
        )}
    </div>
  );
}

/**
 * The right-side panel content for one focused layout. Header + renders +
 * inline BOM (fetched on focus change). The middle content area scrolls; the
 * Total / Add-to-deck bar stays pinned to the bottom so it's always reachable.
 */
function FocusedLayoutView({ layout }: { layout: LayoutListItem }) {
  const [detail, setDetail] = useState<LayoutDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    fetchLayout(layout.code)
      .then((r) => {
        if (!cancelled) setDetail(r.layout);
      })
      .catch(() => {
        // Keep null — BOM section will show its loading/empty fallback.
      });
    return () => {
      cancelled = true;
    };
  }, [layout.code]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
      className="h-full flex flex-col"
    >
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto px-8 lg:px-10 pt-5 pb-6 space-y-4">
        {/* Compact header: code + family left, dims right */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-vari-blue-accessible">
              {layout.familyName}
            </p>
            <h2 className="text-2xl font-semibold text-vari-navy tracking-tight mt-0.5">
              #{formatCode(layout.code)}
            </h2>
          </div>
          <p className="text-xs text-vari-gray text-right shrink-0 mt-1 leading-relaxed">
            {formatDimensions(layout.widthInches, layout.depthInches)}<br />
            {layout.itemCount} items
          </p>
        </div>

        {/* Renders: click a single tile to add JUST that photo to the deck */}
        <RenderSelectionGrid layout={layout} />

        {/* Bill of materials — fetched on focus change */}
        <FocusBom detail={detail} layout={layout} />
      </div>

      {/* Sticky footer: CTA. Stays put while the content above scrolls. */}
      <div className="shrink-0 border-t border-vari-soft-gray bg-white px-8 lg:px-10 py-4 flex justify-end">
        <AddToDeckButton layout={layout} />
      </div>
    </motion.div>
  );
}

function FocusBom({
  detail,
  layout,
}: {
  detail: LayoutDetail | null;
  layout: LayoutListItem;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible">
        Bill of materials
      </h3>

      {detail === null ? (
        <p className="text-xs text-vari-gray">Loading…</p>
      ) : detail.items.length === 0 ? (
        <p className="text-xs text-vari-gray">No items recorded for this layout.</p>
      ) : (
        <div className="rounded-xl border border-vari-soft-gray overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-vari-navy text-white">
                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider">Description</th>
                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider">SKU</th>
                <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wider w-10">Qty</th>
                <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody>
              {detail.items.map((item, i) => (
                <tr
                  key={item.sku}
                  className={i % 2 === 0 ? 'bg-white' : 'bg-vari-beige/25'}
                >
                  <td className="px-4 py-2.5 text-vari-navy leading-snug">{item.description}</td>
                  <td className="px-4 py-2.5 text-vari-gray font-mono text-xs">{item.sku}</td>
                  <td className="px-4 py-2.5 text-right text-vari-navy tabular-nums">{item.quantity}</td>
                  <td className="px-4 py-2.5 text-right text-vari-navy font-medium tabular-nums">
                    {formatCents(item.lineTotalCents)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-vari-navy/8 border-t-2 border-vari-navy/15">
                <td colSpan={3} className="px-4 py-3 text-right font-semibold text-vari-navy text-xs uppercase tracking-wider">
                  Total
                </td>
                <td className="px-4 py-3 text-right font-bold text-vari-navy tabular-nums">
                  {formatCents(layout.totalPriceCents)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

function RenderSelectionGrid({ layout }: { layout: LayoutListItem }) {
  const { isRenderSelected, toggleRenderForLayout } = useDeck();
  const [lightboxKind, setLightboxKind] = useState<RenderKind | null>(null);
  const [copied, setCopied] = useState(false);

  const selectedCount = DECK_RENDER_KINDS.filter((k) => isRenderSelected(layout.code, k)).length;

  const copyDeepLink = async (): Promise<void> => {
    const url = `${window.location.origin}/?layout=${encodeURIComponent(layout.code)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard not available
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible">
          Renders
        </p>
        <div className="flex items-center gap-3">
          {selectedCount > 0 && (
            <p className="text-[11px] text-vari-gray">
              {selectedCount} of {DECK_RENDER_KINDS.length} in deck
            </p>
          )}
          <button
            type="button"
            onClick={copyDeepLink}
            title="Copy a shareable link to this layout"
            className="flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible hover:underline"
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            {copied ? 'Link copied' : 'Copy link'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {GRID_RENDER_KINDS.map((kind) => {
          const selected = isRenderSelected(layout.code, kind);
          const label = RENDER_LABELS[kind as RenderKind];
          return (
            <div
              key={kind}
              className={`relative rounded-md overflow-hidden border-2 transition-all ${
                selected ? 'border-vari-blue-accessible' : 'border-vari-soft-gray'
              }`}
            >
              {/* Click photo to expand */}
              <button
                type="button"
                onClick={() => setLightboxKind(kind)}
                className="block w-full cursor-pointer"
                title={`View ${label}`}
              >
                <PlaceholderRender
                  variant="card"
                  label={label}
                  src={layout.renders[kind]?.thumbUrl ?? null}
                  fit={kind === 'dimensional' ? 'contain' : 'cover'}
                />
              </button>

              {/* Deck toggle — environmental renders only (decks are env-only) */}
              {DECK_RENDER_KINDS.includes(kind) && (
                <button
                  type="button"
                  onClick={() => toggleRenderForLayout(layout, kind)}
                  aria-pressed={selected}
                  title={selected ? `Remove ${label} from deck` : `Add ${label} to deck`}
                  className={`absolute top-2 right-2 w-7 h-7 rounded-md border-2 flex items-center justify-center transition-all shadow ${
                    selected
                      ? 'bg-vari-navy border-vari-navy text-white'
                      : 'bg-white/80 border-vari-soft-gray hover:border-vari-navy text-transparent'
                  }`}
                >
                  <svg viewBox="0 0 10 10" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1.5 5l2.5 2.5 4.5-4" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {lightboxKind !== null && (
          <Lightbox
            src={layout.renders[lightboxKind]?.fullUrl ?? null}
            label={RENDER_LABELS[lightboxKind]}
            onClose={() => setLightboxKind(null)}
            onPrev={() =>
              setLightboxKind((k) => {
                const i = GRID_RENDER_KINDS.indexOf(k as RenderKind);
                return GRID_RENDER_KINDS[(i - 1 + GRID_RENDER_KINDS.length) % GRID_RENDER_KINDS.length] ?? k;
              })
            }
            onNext={() =>
              setLightboxKind((k) => {
                const i = GRID_RENDER_KINDS.indexOf(k as RenderKind);
                return GRID_RENDER_KINDS[(i + 1) % GRID_RENDER_KINDS.length] ?? k;
              })
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ProductSearchBar({
  products,
  selected,
  query,
  dropdownOpen,
  onQueryChange,
  onSelect,
  onClear,
  onDropdownOpen,
  onDropdownClose,
}: {
  products: ProductItem[];
  selected: ProductItem | null;
  query: string;
  dropdownOpen: boolean;
  onQueryChange: (q: string) => void;
  onSelect: (p: ProductItem) => void;
  onClear: () => void;
  onDropdownOpen: () => void;
  onDropdownClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const stop = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener('wheel', stop, { passive: false });
    return () => el.removeEventListener('wheel', stop);
  }, []);

  const filtered = query.trim()
    ? products.filter((p) =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.sku.toLowerCase().includes(query.toLowerCase()),
      )
    : products;

  return (
    <div ref={containerRef} className="relative shrink-0 px-4 py-3 bg-vari-beige/40 backdrop-blur-sm">
      <div className="relative flex items-center gap-2">
        <svg
          className="absolute left-3 w-3.5 h-3.5 text-vari-gray/60 pointer-events-none"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <circle cx="6.5" cy="6.5" r="5" />
          <path d="M10.5 10.5L14 14" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          value={query}
          placeholder="Search by product…"
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={onDropdownOpen}
          onBlur={() => setTimeout(onDropdownClose, 150)}
          className="w-full pl-8 pr-7 py-2 text-sm bg-white/30 text-vari-navy placeholder-vari-gray/50 border border-vari-soft-gray/40 rounded-lg focus:outline-none focus:bg-white/50 focus:border-vari-soft-gray/70 transition-colors"
        />
        {(selected || query) && (
          <button
            type="button"
            onClick={onClear}
            className="absolute right-2.5 text-vari-gray hover:text-vari-navy transition-colors"
            aria-label="Clear search"
          >
            <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="currentColor">
              <path d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z" />
            </svg>
          </button>
        )}
      </div>

      {selected && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-vari-navy text-white text-[10px] font-medium tracking-wide">
            {selected.name}
            <button type="button" onClick={onClear} className="hover:opacity-70 transition-opacity" aria-label="Remove filter">
              <svg viewBox="0 0 16 16" className="w-2.5 h-2.5" fill="currentColor">
                <path d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z" />
              </svg>
            </button>
          </span>
        </div>
      )}

      {dropdownOpen && !selected && filtered.length > 0 && (
        <ul className="absolute left-5 right-5 top-full mt-1 z-50 bg-white rounded-xl border border-vari-soft-gray shadow-xl overflow-hidden max-h-64 overflow-y-auto">
          {filtered.slice(0, 50).map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onMouseDown={() => onSelect(p)}
                className="w-full text-left px-4 py-2.5 hover:bg-vari-beige/50 transition-colors flex items-baseline gap-3"
              >
                <span className="text-sm text-vari-navy truncate">{p.name}</span>
                <span className="text-[10px] text-vari-gray font-mono shrink-0">{p.sku}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

